import { React, useEffect, useState, useRef } from 'react';
import './index2.css';
import '../App.css';
import './get-started.css';
import IntroVideo from '../images/intro_video.mp4';
import Crow_Logo from '../images/crow_electric3.png';
import VideoComponent from './VideoComponent.js';
import { motion, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useScrollBehavior } from './useScrollBehavior.js';
import SEO from '../SEO.js';
import Footer from './footer.js';

const LandingPage_Fix_My_Website = () => {
  
  useScrollBehavior();

  const heroRef = useRef(null);
  const sectionRef = useRef(null);
  const offerRef = useRef(null);

  const [isHeroVisible, setIsHeroVisible] = useState(false);
  const [isSectionVisible, setIsSectionVisible] = useState(false);
  const [isOfferVisible, setIsOfferVisible] = useState(false);

  const subtitleRef = useRef(null);
  const isSubtitleInView = useInView(subtitleRef, { once: true, amount: 0.5 });

  const title = "Fix My Website | Website Redesign & Performance Improvements | Yokai Software";
const description =
  "Yokai Software helps businesses fix outdated websites with stronger design, better speed, improved mobile experience, and conversion-focused structure.";
const canonical = "https://yokaisoftware.com/fix-my-website";
const image = "https://yokaisoftware.com/yokai_apple_touch_icon2.png";

  const schema = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Organization",
      "@id": "https://yokaisoftware.com/#organization",
      "name": "Yokai Software",
      "url": "https://yokaisoftware.com/",
      "logo": "https://yokaisoftware.com/yokai_apple_touch_icon2.png",
      "description": "Yokai Software provides web design, web development, SEO, and digital solutions for businesses."
    },
    {
      "@type": "WebSite",
      "@id": "https://yokaisoftware.com/#website",
      "url": "https://yokaisoftware.com/",
      "name": "Yokai Software",
      "publisher": {
        "@id": "https://yokaisoftware.com/#organization"
      }
    },
    {
      "@type": "WebPage",
      "@id": "https://yokaisoftware.com/fix-my-website#webpage",
      "url": "https://yokaisoftware.com/fix-my-website",
      "name": "Fix My Website | Website Redesign & Performance Improvements | Yokai Software",
      "isPartOf": {
        "@id": "https://yokaisoftware.com/#website"
      },
      "about": {
        "@id": "https://yokaisoftware.com/#organization"
      },
      "description": "Yokai Software helps businesses fix outdated websites with stronger design, better speed, improved mobile experience, and conversion-focused structure."
    },
    {
      "@type": "Service",
      "@id": "https://yokaisoftware.com/fix-my-website#service",
      "serviceType": "Website redesign and website improvement services",
      "provider": {
        "@id": "https://yokaisoftware.com/#organization"
      },
      "areaServed": "US",
      "url": "https://yokaisoftware.com/fix-my-website",
      "description": "Website redesign, speed improvements, mobile optimization, and conversion-focused website improvements for businesses with outdated or underperforming websites."
    }
  ]
};
  const letterAnimation = {
    hidden: { opacity: 0 },
    visible: (i) => ({
      opacity: 1,
      transition: {
        delay: i * 0.05,
        duration: 0.4
      }
    })
  };

  const renderAnimatedLettersByWord = (text, isInView) => {
    const words = text.split(' ');
    let k = 0;

    return words.map((word, wi) => (
      <span className="wordWrap" key={wi}>
        {word.split('').map((char, ci) => (
          <motion.span
            key={`${wi}-${ci}`}
            custom={k++}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            variants={letterAnimation}
            className="char"
          >
            {char}
          </motion.span>
        ))}
        {wi < words.length - 1 ? ' ' : null}
      </span>
    ));
  };

  const fadeUp = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: 'easeOut'
      }
    }
  };

  const handleScroll = () => {
    if (heroRef.current) {
      const rect = heroRef.current.getBoundingClientRect();
      setIsHeroVisible(rect.top < window.innerHeight - 80);
    }

    if (sectionRef.current) {
      const rect = sectionRef.current.getBoundingClientRect();
      setIsSectionVisible(rect.top < window.innerHeight - 120);
    }

    if (offerRef.current) {
      const rect = offerRef.current.getBoundingClientRect();
      setIsOfferVisible(rect.top < window.innerHeight - 120);
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <div id="yokai_landing_wrapper">
  <SEO
    title={title}
    description={description}
    canonical={canonical}
    image={image}
    schema={schema}
  />

  <VideoComponent videoSrc={IntroVideo} />

  <div className="yokai_landing_module" id="yokai_landing_hero">
    <div id='yokai_landing_heading_container'>
      <div id='yokai_landing_heading_main' ref={subtitleRef}>
        {renderAnimatedLettersByWord("Your Website Is", isSubtitleInView)}
      </div>

      <div id='yokai_landing_heading_accent'>
        {renderAnimatedLettersByWord("Costing You Business", isSubtitleInView)}
      </div>

      <div id='yokai_landing_heading_sub'>
        {renderAnimatedLettersByWord("Slow, outdated sites lose customers every day..", isSubtitleInView)}
      </div>

      <motion.div
        id="yokai_landing_intro_text"
        ref={heroRef}
        initial="hidden"
        animate={isHeroVisible ? "visible" : "hidden"}
        variants={fadeUp}
      >
        <p>
            If your website feels outdated, loads slowly, or makes it hard for 
            visitors to take action, it may be pushing customers away. Yokai Software 
            helps businesses improve design, speed, mobile usability, and conversion 
            structure so the website works harder for the business.
        </p>

        <div id="yokai_landing_cta_row">
          <Link to="/ContactPage" className="yokai_landing_cta_primary">
            Get My Website
          </Link>
          <Link to="/ContactPage" className="yokai_landing_cta_secondary">
            Free Website Audit
          </Link>
        </div>

        <div id="yokai_landing_pill_row">
          <div className="yokai_landing_pill">Custom Design</div>
          <div className="yokai_landing_pill">SEO Focused</div>
          <div className="yokai_landing_pill">Mobile First</div>
        </div>
      </motion.div>
    </div>

    <div id='yokai_landing_logo_container'>
      <div id="yokai_landing_logo">
        <img src={Crow_Logo} alt="Yokai Software logo" className="yokai_landing_logo_img" />
      </div>
    </div>
  </div>

      <div className="module" id="landing_module_problem">
        <div id="landing_problem_container" ref={sectionRef}>
          <motion.div
            id="landing_problem_heading"
            initial="hidden"
            animate={isSectionVisible ? "visible" : "hidden"}
            variants={fadeUp}
          >
            <h1>Why an Outdated Website Can Cost You Customers</h1>
            <p>
                Many business websites look acceptable on the surface but still 
                underperform. Slow loading, weak mobile layouts, outdated visuals, 
                confusing page flow, and weak calls to action can reduce trust and 
                hurt conversions.
            </p>
          </motion.div>

          <div id="landing_problem_cards">
            <motion.div
              className="landing_problem_card"
              initial="hidden"
              animate={isSectionVisible ? "visible" : "hidden"}
              variants={fadeUp}
              transition={{ delay: 0.15 }}
            >
              <h2>Slow Load Times</h2>
                <p>
                    A slow website creates friction immediately. Visitors leave 
                    faster when pages do not load smoothly.
                </p>
            </motion.div>

            <motion.div
              className="landing_problem_card"
              initial="hidden"
              animate={isSectionVisible ? "visible" : "hidden"}
              variants={fadeUp}
              transition={{ delay: 0.3 }}
            >
              <h2>Outdated Design</h2>
                <p>
                    An outdated look can weaken credibility and make the business 
                    feel less trustworthy than competitors.
                </p>
            </motion.div>

            <motion.div
              className="landing_problem_card"
              initial="hidden"
              animate={isSectionVisible ? "visible" : "hidden"}
              variants={fadeUp}
              transition={{ delay: 0.45 }}
            >
              <h2>Weak Conversion Flow</h2>
                <p>
                    If the layout, messaging, and calls to action are unclear, 
                    visitors will not know what to do next.
                </p>
            </motion.div>
          </div>
        </div>
      </div>

      <div className="module" id="landing_module_solution">
        <div id="landing_solution_container">
          <div id="landing_solution_left">
            <h1>Website Improvements Built for Performance and Conversions</h1>

            <div className="landing_solution_item">
              <h2>Stronger website design for your business</h2>
                <p>
                    Your website should look current, intentional, and trustworthy 
                    instead of dated or generic.
                </p>
            </div>

            <div className="landing_solution_item">
              <h2>Clearer structure and page flow</h2>
                <p>
                    Better hierarchy, content organization, and section flow help 
                    users understand the business and move toward action.
                </p>
            </div>

            <div className="landing_solution_item">
              <h2>Better mobile experience</h2>
                <p>
                    Many outdated websites break down on mobile. A stronger mobile 
                    layout helps keep users engaged.
                </p>
            </div>

            <div className="landing_solution_item">
              <h2>Conversion-focused improvements</h2>
                <p>
                    Better headlines, stronger calls to action, and clearer page flow 
                    help turn more visitors into leads.
                </p>
            </div>
          </div>

          <div id="landing_solution_right">
            <div className="landing_stat_box">
              <span>Built for</span>
              <h2>Lead Generation</h2>
            </div>
            <div className="landing_stat_box">
              <span>Designed for</span>
              <h2>Trust + Clarity</h2>
            </div>
            <div className="landing_stat_box">
              <span>Structured for</span>
              <h2>Search Visibility</h2>
            </div>
          </div>
        </div>
      </div>

      <div className="module" id="landing_module_offer">
        <motion.div
          id="landing_offer_box"
          ref={offerRef}
          initial="hidden"
          animate={isOfferVisible ? "visible" : "hidden"}
          variants={fadeUp}
        >
          <h1>Get Started Without Making It Complicated</h1>
            <p>
               Whether your site needs a redesign, better speed, stronger mobile 
               behavior, or a clearer conversion path, Yokai Software can identify 
               the weaknesses and improve the parts that are costing you business.
            </p>

          <div id="landing_offer_options">
            <div className="landing_offer_option">
              <h2>One-Time Build</h2>
              <p>
                Ideal if you want a complete website project planned, built, and launched.
              </p>
            </div>

            <div className="landing_offer_option">
              <h2>Ongoing Support</h2>
              <p>
                Ideal if you want updates, improvements, optimization, and continued help after launch.
              </p>
            </div>
          </div>

          <div id="landing_offer_button_row">
            <Link to="/ContactPage" className="landing_cta_button_primary">
              Start Your Project
            </Link>
          </div>
        </motion.div>
      </div>

      <div className="module" id="landing_module_final">
        <div id="landing_final_container">
          <h1>Let's Build Something That Actually Works</h1>
          <p>
            A stronger website improves trust, supports visibility, and helps turn traffic into real business.
          </p>
          <Link to="/ContactPage" className="landing_cta_button_primary">
            Contact Yokai Software
          </Link>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default LandingPage_Fix_My_Website;